import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAccountContext } from './accounts';

/** The search parameter a background notification's click carries. */
export const ACCOUNT_PARAM = 'account';

/**
 * Honours `?account=<id>` on a mail, calendar or tasks route, then removes it.
 *
 * The service worker opens `/mail/<folder id>/<message id>?account=<id>` when
 * a background notification is clicked. The path alone is not enough: folder
 * and message ids are only unique within one account, so the route means the
 * right message only once that account is the active one. A calendar
 * reminder's click opens `/calendar/day?event=…&at=…&account=<primary id>` for
 * the same reason: the calendar shows the active account's events, and the
 * event id is the primary's. The calendar waits for this parameter to be gone
 * before it consumes its own (routes/calendar/CalendarView.tsx), so the two
 * replace navigations cannot undo each other. A task reminder's click
 * (realtime/taskReminders.ts, and the server's push) opens
 * `/tasks?list=…&task=…&account=<id>` — a connected mailbox's tasks too — and
 * the Tasks screen likewise reads `list` and `task` only once this is gone.
 *
 * - An id that is not one of this owner's mailboxes is ignored: the parameter
 *   comes from a URL, and a URL is not a way to name someone else's mailbox
 *   (the server would refuse it anyway; this just does not try).
 * - The parameter is removed with a replace navigation, keeping the path and
 *   every other parameter, so Back does not return to it and a reload does not
 *   switch accounts again after the user has moved on.
 * - Nothing happens until the account list has loaded, so a cold start from a
 *   notification does not discard the parameter before it can be matched.
 */
export function useAccountSearchParam(enabled: boolean): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const { accounts, isLoading, setActiveAccountId } = useAccountContext();
  const wanted = enabled ? searchParams.get(ACCOUNT_PARAM) : null;

  useEffect(() => {
    if (wanted === null || isLoading) return;
    if (accounts.some((account) => account.id === wanted)) setActiveAccountId(wanted);
    setSearchParams(
      (params) => {
        params.delete(ACCOUNT_PARAM);
        return params;
      },
      { replace: true },
    );
  }, [wanted, isLoading, accounts, setActiveAccountId, setSearchParams]);
}
